import { useEffect, useRef, useState, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { CtyButton } from "../button/CtyButton";
import { SchematicSvgViewer } from "./schematicsvgviewer";
import { PartsSvgViewer } from "./partssvgviewer";
import ZipFileExplorer, { ComponentGroup, ComponentNode } from "./partszipfileviewer";
import CtyHeader from "../header/Cty-Header";
import { applicationType, ApplicationType, isBrowserExtension } from "../utils/constants";
import { decompressedData } from "../utils/commonutils";
import { DitaHtmlViewer } from "./DitaHtmlViewer";
import { DitaZipFileViewer } from "./DitaZipFileViewer";
import { useGetPdfLinkForConversationsMutation, useGetGuestPdfLinkForConversationsMutation, useGetPdfLinkMutation, useGetDetailsQuery, type OrganizationDetails } from '@circuitry-ai/doc-data';
import { useSelector } from 'react-redux';
import type { RootState } from '@circuitry-ai/doc-data';
import { useTranslation } from "react-i18next";
import { MetadataInfoButton } from "./MetadataInfoButton";

// Define strongly typed interfaces for zip file structure
interface FileNode {
    id: string;
    name: string;
    is_folder: false;
    s3_key: string;
    source_id: string;
    page_labels?: number[];
    children: never[];
}

interface FolderNode {
    id: string;
    name: string;
    is_folder: true;
    children: HierarchyNode[];
}

type HierarchyNode = FileNode | FolderNode;

// Helper function to render appropriate SVG viewer
function renderSvgViewer(svgUrl: string, sourceIdData: any, svgType: string) {
    switch (svgType) {
        case 'schematicsvg':
            return <SchematicSvgViewer svgUrl={svgUrl} sourceIdData={sourceIdData} />;
        case 'partssvg':
            return <PartsSvgViewer svgUrl={svgUrl} isFileViewer={true} />;
        default:
            return <SchematicSvgViewer svgUrl={svgUrl} sourceIdData={sourceIdData} />;
    }
}

// Helper function to check if a file is viewable
const isViewableFile = (fileName: string): boolean => {
    const viewableExtensions = ['.pdf', '.svg', '.dwg'];
    return viewableExtensions.some(ext => fileName.toLowerCase().endsWith(ext));
};

// Separate component for handling zip files
interface ZipViewerProps {
    hierarchyData: HierarchyNode[];
    svgUrl: string;
    sourceIdData: any;
    advisorId: string;
    sourceObject?: any;
    metadataSourceId?: string;
}

function ZipViewer({ hierarchyData, svgUrl, sourceIdData, advisorId, sourceObject, metadataSourceId }: ZipViewerProps) {
    useMemo(() => {
        const processNode = (node: HierarchyNode): ComponentGroup[] => {
            if (node.is_folder && node.children) {
                const components: ComponentNode[] = node.children
                    .filter((child): child is FileNode => !child.is_folder)
                    .map(child => ({
                        name: child.name,
                        path: child.s3_key ?? ''
                    }));

                if (components?.length > 0) {
                    return [{
                        component_name: node.name,
                        components
                    }];
                }
            }
            return [];
        };

        return hierarchyData.flatMap(processNode);
    }, [hierarchyData]);

    return (
        <ZipFileExplorer
            hierarchyData={hierarchyData}
            svgUrl={svgUrl}
            sourceIdData={sourceIdData}
            advisorId={advisorId ?? ''}
            sourceObject={sourceObject}
            metadataSourceId={metadataSourceId}
        />
    );
}

function FileViewer() {
    const { t } = useTranslation();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const tabKey = queryParams.get('tabKey');
    const origin = queryParams.get('origin'); // Get the origin from URL
    console.log("origin", origin);
    console.log("applicationType", applicationType());
    const dataParam = queryParams.get('data');
    const { advisorId } = useParams();
    const hasOpenedFile = useRef(false); // To prevent double opening

    // Get auth tokens for DITA API calls
    const { token: authToken } = useSelector((state: RootState) => state.auth);
    const { guestToken } = useSelector((state: RootState) => state.advisor);

    // Initialize API hooks
    const [getPdfLinkForConversations] = useGetPdfLinkForConversationsMutation();
    const [getGuestPdfLink] = useGetGuestPdfLinkForConversationsMutation();
    const [getPdfLinkV2] = useGetPdfLinkMutation(); // For DITAMAP files - calls /api/v2/knowledge/view

    // For DITA files, we MUST use the conversations/knowledge endpoint
    // NOT the agent presigned URL endpoint (which is for general knowledge viewing)
    const apiToUse = guestToken && !authToken ? getGuestPdfLink : getPdfLinkForConversations;
    const [extensionPdfData, setExtensionPdfData] = useState<any>(null);
    // Retrieve from chrome.storage.session for extension context
    useEffect(() => {
        if (applicationType() === ApplicationType.BROWSER_EXTENSION && tabKey && (globalThis as any).chrome?.storage?.session) {
            (globalThis as any).chrome.storage.session.get(`pdfData-${tabKey}`).then((result: any) => {
                if (result[`pdfData-${tabKey}`]) {
                    console.log('✅ FileViewer: Found data in chrome.storage.session');
                    const decompressed = decompressedData(JSON.parse(result[`pdfData-${tabKey}`]));
                    setExtensionPdfData(decompressed);
                } else {
                    console.warn('⚠️ FileViewer: No data found in chrome.storage.session');
                }
            }).catch((error: any) => {
                console.error('❌ FileViewer: Error reading from chrome.storage.session:', error);
            });
        }
    }, [tabKey]);
    // Use useMemo to make pdfData reactive to extensionPdfData changes
    const pdfData = useMemo(() => {
        if (applicationType() === ApplicationType.WEB) {
            // Try to get data from session storage first
            console.log('🔍 FileViewer: Looking for pdfData-' + tabKey + ' in sessionStorage');
            const storedData = sessionStorage.getItem(`pdfData-${tabKey}`);
            if (storedData) {
                console.log('✅ FileViewer: Found data in sessionStorage');
                const decompressed = decompressedData(JSON.parse(storedData));
                console.log('📦 FileViewer: Decompressed pdfData:', { fileType: decompressed.fileType, fileName: decompressed.fileName });
                return decompressed;
            } else {
                console.warn('⚠️ FileViewer: No data found in sessionStorage for key:', `pdfData-${tabKey}`);
            }
            // If session storage is not available (e.g., third-party domain), fallback to dataParam
            if (dataParam) {
                console.log("Session storage not available, using dataParam for third-party domain", dataParam);
                return decompressedData(dataParam);
            }
            console.error('❌ FileViewer: No pdfData available from sessionStorage or dataParam');
            return {};
        } else if (applicationType() === ApplicationType.BROWSER_EXTENSION) {
            // Extension context: Use data from chrome.storage.session (loaded asynchronously)
            if (extensionPdfData) {
                return extensionPdfData;
            }
            // Fallback to dataParam while loading
            return dataParam ? decompressedData(dataParam) : {};
        } else {
            // Widget context: Try sessionStorage first (same as WEB)
            const storedData = sessionStorage.getItem(`pdfData-${tabKey}`);
            if (storedData) {
                console.log('✅ FileViewer: Found data in sessionStorage (widget)');
                return decompressedData(JSON.parse(storedData));
            }
            // Fallback to dataParam
            return dataParam ? decompressedData(dataParam) : {};
        }
    }, [tabKey, dataParam, extensionPdfData]);

    // Ensure the authentication token is properly set up for this component
    useEffect(() => {
        // If we have the sourceObject from the parent window, we can use it for authentication
        if (pdfData.sourceObject && pdfData.sourceObject.advisor_id) {
            // Log that we have the source object for debugging
            console.log("Source object available for authentication", pdfData.sourceObject.advisor_id);
        }

        // If we came from a different origin, we might need to handle token transfer
        if (origin && origin !== window.location.origin) {
            console.log("Cross-origin file view, authenticated context might be needed");
        }
    }, [pdfData, origin]);

    const fileType = pdfData.fileType ?? 'pdf';
    const fileName = pdfData.fileName ?? '';
    const metadataSourceId = useMemo(() => {
        const direct = pdfData?.sourceId ?? pdfData?.source_id ?? '';
        if (direct) return String(direct);

        const responseSource = pdfData?.responseSourceData;
        if (Array.isArray(responseSource)) {
            const fromArray = responseSource.find((entry: { source_id?: string }) => entry?.source_id)?.source_id;
            if (fromArray) return String(fromArray);
        }
        if (typeof responseSource === 'string' && responseSource) {
            return responseSource;
        }

        return pdfData?.sourceObject?.source_id ? String(pdfData.sourceObject.source_id) : '';
    }, [pdfData]);
    const isDitaFileType = fileType === 'ditahtml' || fileType === 'ditazipfile' || fileType === 'dita';
    const { data: tenantDetailsResponse } = useGetDetailsQuery(undefined, {
        skip: !isDitaFileType || !authToken,
        refetchOnMountOrArgChange: true,
    });
    const cachedTenantDetailsResponse = useSelector((state: RootState) => state.tenant.tenantDetailsResponse);
    const tenantCssUrl = useMemo(() => {
        const response = tenantDetailsResponse ?? cachedTenantDetailsResponse;
        const tenantDetails = ((response as { data?: OrganizationDetails })?.data ?? response) as
            | OrganizationDetails
            | undefined;
        const cssUrl = (tenantDetails?.meta_data as Record<string, unknown> | undefined)?.css_url;
        return typeof cssUrl === 'string' ? cssUrl.trim() : '';
    }, [tenantDetailsResponse, cachedTenantDetailsResponse]);
    const [isFullPdfOpen, setIsFullPdfOpen] = useState(false);
    const currentFileUrl = useRef(pdfData.initialPdfUrl ?? pdfData.fullPdfUrl);
    useEffect(() => {
        currentFileUrl.current = pdfData.initialPdfUrl ?? pdfData.fullPdfUrl;
    }, [pdfData]);
    const navigate = useNavigate();

    const handlePdfChanger = () => {
        const pageNumber = pdfData.pageLabel?.[0] ?? '1'; // Current page or default to 1

        if (isFullPdfOpen) {
            // Switch back to single-page view using initial (single-page) URL
            currentFileUrl.current = pdfData.initialPdfUrl ?? pdfData.fullPdfUrl;
        } else {
            // Switch to full PDF view using fullPdfUrl
            currentFileUrl.current = pdfData.fullPdfUrl ?? pdfData.initialPdfUrl;
        }

        // Toggle view mode without changing the route to avoid cancelling in-flight requests
        setIsFullPdfOpen(prev => !prev);
    };

    useEffect(() => {
        if (!['pdf', 'svg', 'parts_svg', 'zip', 'dita', 'ditahtml', 'ditazipfile', 'ditaresource', 'png', 'jpg', 'jpeg'].includes(fileType) && currentFileUrl.current && !hasOpenedFile.current) {
            hasOpenedFile.current = true; // Mark as opened
            window.open(currentFileUrl.current, "_blank");
        }
    }, [fileType]);
    const pageNumber = Array.isArray(pdfData.pageLabel) ? pdfData.pageLabel[0] : pdfData.pageLabel ?? '1';
    const pdfUrl = isFullPdfOpen
        ? currentFileUrl.current+`#page=${pageNumber}`  // View entire page we added page number to the url (AI-4634 fix)
        : currentFileUrl.current; // View single page we removed page number from the url (NOAH-170 fix)

    const getBtnName = () => {
        if (isFullPdfOpen){
            return t('View Single Page of ') + fileName;
        } else {
            return t('View Entire ') + fileName;
        }
    };

    const renderFileViewer = () => {
        switch (fileType) {
            case 'pdf':
                return (
                    <div className="h-full w-full">
                        <div className={`w-full px-3 py-2 flex items-center justify-between gap-2`}>
                            <div className="min-w-0">
                                {(pdfData.initialPdfUrl !== pdfData.fullPdfUrl) && <CtyButton size="md" id="pdf-file-name" color="primary" disableAnimation={true} disableRipple={true} className="max-w-[70vw] truncate" onPress={handlePdfChanger}>{getBtnName()}</CtyButton>}
                                {(pdfData.initialPdfUrl === pdfData.fullPdfUrl) && <p className="border-radius-0 bg-[white] font-medium text-base text-black truncate">{fileName}</p>}
                            </div>
                            <MetadataInfoButton sourceId={metadataSourceId} />
                        </div>
                        <iframe src={pdfUrl} className="w-full h-[100vh]" title="pdf"></iframe>
                    </div>
                );
            case 'svg':
                return (
                    <div className="h-full w-full bg-[#fff]">
                        {renderSvgViewer(currentFileUrl.current, pdfData?.responseSourceData ?? [], 'schematicsvg')}
                    </div>
                );
            case 'parts_svg':
                return (
                    <div className="h-full w-full bg-[#fff]">
                        {pdfData.fileName.endsWith('svg')
                            ? renderSvgViewer(currentFileUrl.current, pdfData?.responseSourceData ?? [], 'partssvg')
                            : <iframe src={currentFileUrl.current} className="w-full h-[100vh]" title="pdf"></iframe>
                        }
                    </div>
                );
            case 'zip':
                return (
                    <div className="h-full w-full bg-[#fff]">
                        <ZipViewer
                            hierarchyData={pdfData?.zipSchema ?? []}
                            svgUrl={currentFileUrl.current}
                            sourceIdData={pdfData?.responseSourceData ?? []}
                            advisorId={advisorId ?? pdfData?.sourceObject?.advisor_id ?? ''}
                            sourceObject={pdfData?.sourceObject}
                            metadataSourceId={metadataSourceId}
                        />
                    </div>
                );
            case 'ditahtml':
                // Single DITA HTML file from converse section
                // The presigned URL is already fetched and stored in currentFileUrl
                // DitaHtmlViewer fetches HTML from this URL and processes data-dita-source_id
                if (!apiToUse) {
                    console.error('❌ FileViewer: apiToUse is undefined for ditahtml');
                    return (
                        <div className="flex items-center justify-center h-full w-full">
                            <p className="text-red-500">API configuration error. Please refresh the page.</p>
                        </div>
                    );
                }
                return (
                    <div className="h-full w-full bg-[#fff] overflow-y-auto">
                        <div className="flex-shrink-0 w-full px-4 py-1 bg-content1 border-b border-default-200 flex items-center justify-between gap-2">
                            <p className="border-radius-0 bg-[white] font-medium text-base text-black truncate">{fileName}</p>
                            <MetadataInfoButton sourceId={metadataSourceId} />
                        </div>
                        <DitaHtmlViewer
                            htmlUrl={currentFileUrl.current}
                            getPdfLink={apiToUse}
                            advisorId={advisorId ?? pdfData?.advisorId ?? pdfData?.sourceObject?.advisor_id ?? ''}
                            lazyLoad={false}
                            concurrencyLimit={5}
                            onError={(error, sourceId) => {
                                console.error('DITA HTML Viewer error:', error, 'for source_id:', sourceId);
                            }}
                            className="h-full"
                        />
                    </div>
                );
            case 'ditazipfile':
                // DITA ZIP file with table of contents
                return (
                    <DitaZipFileViewer
                        ditaJsonUrl={pdfData?.ditaJsonUrl ?? ''}
                        fileName={fileName}
                        advisorId={advisorId ?? pdfData?.advisorId ?? pdfData?.sourceObject?.advisor_id ?? ''}
                        getPdfLink={getPdfLinkV2}
                        metadataSourceId={metadataSourceId}
                        initialTopicSourceId={pdfData?.initialTopicSourceId}
                    />
                );
            default:
                return (
                    <div className="h-full w-full">
                        <iframe src={currentFileUrl.current} className="w-full h-[100vh]" title="pdf"></iframe>
                    </div>
                );
        }
    };
    return (
        <div className="h-full w-full">
            {fileType === 'zip' && <CtyHeader isFileViewer={true} />}
            {isDitaFileType && tenantCssUrl ? (
                <link
                    id="dita-tenant-css"
                    key={tenantCssUrl}
                    rel="stylesheet"
                    type="text/css"
                    href={tenantCssUrl}
                />
            ) : null}
            {renderFileViewer()}
        </div>
    );
}

export { FileViewer };
